import React, { useState, useEffect, useRef } from "react";
import { LiveWave, WavePlayer } from "./Waveform";

export const MAX_AUDIO_MB = 25;
const MAX_SECONDS = 5 * 60;

// Extension → the type stored in the bucket (it accepts only these).
const AUDIO_TYPES = {
  mp3: "audio/mpeg",
  m4a: "audio/mp4",
  mp4: "audio/mp4",
  aac: "audio/aac",
  wav: "audio/wav",
  ogg: "audio/ogg",
  oga: "audio/ogg",
  webm: "audio/webm",
};
const EXT_FOR = {
  "audio/mpeg": "mp3",
  "audio/mp3": "mp3",
  "audio/mp4": "m4a",
  "audio/x-m4a": "m4a",
  "audio/m4a": "m4a",
  "audio/aac": "aac",
  "audio/x-aac": "aac",
  "audio/wav": "wav",
  "audio/x-wav": "wav",
  "audio/wave": "wav",
  "audio/ogg": "ogg",
  "audio/webm": "webm",
};

// { type, ext } to upload with, or null if it isn't an audio file we take.
export function audioType(blob, name = "") {
  const fromName = AUDIO_TYPES[(name.split(".").pop() || "").toLowerCase()];
  const raw = (blob.type || "").split(";")[0].trim().toLowerCase();
  const ext = EXT_FOR[raw] || (fromName && EXT_FOR[fromName]);
  return ext ? { type: AUDIO_TYPES[ext], ext } : null;
}

// The browser's own recorder: Chrome and Firefox give webm/ogg, Safari gives mp4.
function pickRecorderType() {
  if (typeof MediaRecorder === "undefined") return null;
  const options = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];
  return options.find((t) => MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(t)) || "";
}

const size = (b) => (b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`);
const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

// Optional recording of the letter read aloud: record here, or upload a file.
// onChange({ blob, name, source: "recorded" | "uploaded" }) or onChange(null).
function LetterAudio({ value, onChange, onRecording, error, setError }) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [live, setLive] = useState(null); // the microphone stream while recording
  const recorder = useRef(null);
  const chunks = useRef([]);
  const stream = useRef(null);
  const timer = useRef(null);
  const fileInput = useRef(null);
  useEffect(() => onRecording && onRecording(recording), [recording, onRecording]);

  const canRecord =
    typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia && pickRecorderType() !== null;

  // Leaving the page mid-recording: let go of the microphone.
  useEffect(
    () => () => {
      clearInterval(timer.current);
      stream.current?.getTracks().forEach((t) => t.stop());
    },
    []
  );

  const stop = () => {
    clearInterval(timer.current);
    if (recorder.current && recorder.current.state !== "inactive") recorder.current.stop();
  };

  const start = async () => {
    setError("");
    try {
      stream.current = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      setError(
        err.name === "NotAllowedError"
          ? "The browser did not allow the microphone. Allow it for this site and try again, or upload a recording instead."
          : "Could not open the microphone. You can upload a recording instead."
      );
      return;
    }
    const mimeType = pickRecorderType();
    const rec = new MediaRecorder(stream.current, mimeType ? { mimeType } : undefined);
    chunks.current = [];
    rec.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
    rec.onstop = () => {
      stream.current?.getTracks().forEach((t) => t.stop());
      setLive(null);
      setRecording(false);
      setError("");
      const blob = new Blob(chunks.current, { type: rec.mimeType || mimeType || "audio/webm" });
      if (!blob.size) return setError("Nothing was recorded. Please try again.");
      if (!audioType(blob)) return setError("This browser recorded in a format we can't take. Please upload a recording instead.");
      onChange({ blob, name: "recording", source: "recorded" });
    };
    recorder.current = rec;
    setLive(stream.current);
    rec.start(1000);
    setSeconds(0);
    setRecording(true);
    onChange(null);
    const began = Date.now();
    timer.current = setInterval(() => {
      const s = (Date.now() - began) / 1000;
      setSeconds(s);
      if (s >= MAX_SECONDS) stop();
    }, 250);
  };

  const pickFile = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    setError("");
    if (!file) return;
    if (!audioType(file, file.name)) return setError("Please choose an MP3, M4A, WAV, AAC, OGG or WEBM audio file.");
    if (file.size > MAX_AUDIO_MB * 1024 * 1024)
      return setError(`That recording is larger than ${MAX_AUDIO_MB} MB. Please send a shorter one.`);
    onChange({ blob: file, name: file.name, source: "uploaded" });
  };

  return (
    <div className="rounded-lg border border-earth-200 bg-earth-50 p-4 space-y-3">
      <div>
        <p className="block text-sm font-medium text-gray-700">Read it in your own voice (optional)</p>
        <p className="font-krushna text-sm text-sritaru-800">మీ ఉత్తరాన్ని మీ గొంతుతో చదివి వినిపించండి</p>
        <p className="text-xs text-gray-500 mt-1">
          Record yourself reading the letter right here, or upload a recording from your phone
          (MP3, M4A, WAV, up to {MAX_AUDIO_MB} MB). A quiet room works best.
        </p>
      </div>

      {recording ? (
        <div className="space-y-2">
          <div className="bg-white rounded-lg border border-red-200 px-3 py-2">
            <LiveWave stream={live} />
          </div>
          <div className="flex items-center gap-3">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-red-600" />
            </span>
            <span className="font-mono text-sm text-gray-800">
              {fmt(seconds)} / {fmt(MAX_SECONDS)}
            </span>
            <button
              type="button"
              onClick={stop}
              className="ml-auto bg-red-600 text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-red-700"
            >
              &#9632; Stop
            </button>
          </div>
        </div>
      ) : value ? (
        <div className="space-y-2">
          <WavePlayer blob={value.blob} />
          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
            <span>
              {value.source === "recorded" ? "Your recording" : value.name} ·{" "}
              {size(value.blob.size)}
            </span>
            <button type="button" onClick={() => onChange(null)} className="ml-auto text-red-500 hover:text-red-700">
              Remove
            </button>
            {canRecord && (
              <button type="button" onClick={start} className="text-sritaru-700 hover:text-sritaru-900 font-medium">
                Record again
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row gap-2">
          {canRecord && (
            <button
              type="button"
              onClick={start}
              className="flex-1 bg-sritaru-600 text-white text-sm font-semibold px-4 py-2.5 rounded-lg hover:bg-sritaru-700"
            >
              &#127908; Record with microphone
            </button>
          )}
          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            className="flex-1 bg-white border border-sritaru-300 text-sritaru-800 text-sm font-semibold px-4 py-2.5 rounded-lg hover:bg-sritaru-50"
          >
            &#128190; Upload audio file
          </button>
        </div>
      )}
      <input
        ref={fileInput}
        type="file"
        accept="audio/*,.mp3,.m4a,.wav,.aac,.ogg,.webm"
        onChange={pickFile}
        className="hidden"
      />
      {error && <p className="text-red-500 text-xs">{error}</p>}
    </div>
  );
}

export default LetterAudio;
